import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { PageHeader, Badge, Select, Money, DateText, Button, Modal, Field, Input, Textarea, Card, EmptyState } from '../components/ui';
import { Can } from '../components/Can';

type R = Record<string, any>;
type Line = { description: string; unitPrice: string; taxCode: string };
const today = () => new Date().toISOString().slice(0, 10);
const blankLine = (): Line => ({ description: '', unitPrice: '', taxCode: '' });

/**
 * Standing charges that raise themselves — parking, storage, a fixed service charge,
 * a management fee between group companies. Rent is not here: the tenancy's own rent
 * schedule already knows what is due and when.
 *
 * A run that is late catches up, dating each invoice when it was DUE rather than
 * today, so back-dated income lands in the right period.
 */
export default function RecurringPage() {
  const qc = useQueryClient();
  const [entityId, setEntityId] = useState('');
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<R | null>(null);
  const [runResult, setRunResult] = useState<R | null>(null);
  const [form, setForm] = useState({ name: '', invoiceType: 'RECHARGE', tenancyId: '', frequency: 'MONTHLY', intervalCount: '1', startDate: today(), endDate: '', dueDays: '14', autoIssue: false, notes: '' });
  const [lines, setLines] = useState<Line[]>([blankLine()]);

  const entities = useQuery({ queryKey: ['entities'], queryFn: async () => (await api.get<R[]>('/entities')).data });
  const tenancies = useQuery({ queryKey: ['tenancies-picker'], queryFn: async () => (await api.get<R[]>('/tenancies', { status: 'ACTIVE', perPage: 200 })).data, enabled: creating });
  const taxCodes = useQuery({ queryKey: ['tax-codes'], queryFn: async () => (await api.get<R>('/accounts/tax-codes')).data });
  const list = useQuery({ queryKey: ['recurring', entityId, status], queryFn: async () => (await api.get<R[]>('/recurring-invoices', { entityId: entityId || undefined, status: status || undefined })).data });
  const detail = useQuery({ queryKey: ['recurring-one', open?.id], queryFn: async () => (await api.get<R>(`/recurring-invoices/${open!.id}`)).data, enabled: !!open });
  const target = entityId || entities.data?.[0]?.id || '';

  const refresh = () => { void qc.invalidateQueries({ queryKey: ['recurring'] }); if (open) void qc.invalidateQueries({ queryKey: ['recurring-one', open.id] }); };
  const selectedTenancy = tenancies.data?.find((t) => t.id === form.tenancyId);

  const create = useMutation({
    mutationFn: async () => (await api.post<R>('/recurring-invoices', {
      entityId: target, name: form.name, invoiceType: form.invoiceType,
      tenancyId: form.tenancyId || undefined, customerUserId: selectedTenancy?.tenants?.[0]?.id,
      propertyId: selectedTenancy?.propertyId, unitId: selectedTenancy?.unitId,
      frequency: form.frequency, intervalCount: Number(form.intervalCount),
      startDate: form.startDate, endDate: form.endDate || undefined,
      dueDays: Number(form.dueDays), autoIssue: form.autoIssue, notes: form.notes || undefined,
      lines: lines.filter((l) => l.description && l.unitPrice).map((l) => ({ description: l.description, unitPriceMinor: Math.round(Number(l.unitPrice) * 100), taxCode: l.taxCode || undefined })),
    })).data,
    onSuccess: () => { setCreating(false); setLines([blankLine()]); refresh(); },
  });
  const setStatusMutation = useMutation({ mutationFn: async ({ id, next }: { id: string; next: string }) => api.patch(`/recurring-invoices/${id}`, { status: next }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: async (id: string) => api.delete(`/recurring-invoices/${id}`), onSuccess: () => { setOpen(null); refresh(); } });
  const run = useMutation({
    mutationFn: async (dryRun: boolean) => (await api.post<R>('/recurring-invoices/run', { entityId: target, dryRun })).data,
    onSuccess: (r) => { setRunResult(r); if (!r.dryRun) refresh(); },
  });

  const region = 'UK';
  const codes: R[] = taxCodes.data?.[region] ? Object.values(taxCodes.data[region].codes) : [];
  const dueNow = list.data?.filter((t) => t.isDue).length ?? 0;

  return (
    <div>
      <PageHeader
        title="Recurring invoices"
        subtitle="Standing charges that raise themselves. Rent is not here — the tenancy's rent schedule already handles that."
        actions={<Can do="recurringInvoices.create"><div className="flex items-center gap-2">
          <Can do="recurringInvoices.run"><Button variant="secondary" icon="Play" onClick={() => run.mutate(true)} disabled={run.isPending || !target}>Preview run</Button></Can>
          <Button icon="Plus" onClick={() => setCreating(true)}>New template</Button>
        </div></Can>}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select value={entityId} onChange={(e) => setEntityId(e.target.value)}><option value="">All companies</option>{entities.data?.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}</Select>
        <Select value={status} onChange={(e) => setStatus(e.target.value)}><option value="">Any status</option><option value="ACTIVE">Active</option><option value="PAUSED">Paused</option><option value="ENDED">Ended</option></Select>
        {dueNow > 0 && <span className="rounded-full bg-amber-50 px-3 py-1 text-[12px] text-amber-800">{dueNow} due to be raised</span>}
      </div>

      {!list.data?.length ? (
        <EmptyState icon="RefreshCw" title="No recurring charges" body="Set up a template for a parking bay, a storage unit or a standing service charge." />
      ) : (
        <div className="surface overflow-hidden">
          <table className="w-full text-[13px]">
            <thead><tr className="border-b border-ink-100 bg-ink-50/70 text-left text-[10.5px] uppercase tracking-[0.14em] text-ink-400">
              <th className="px-4 py-2.5">Template</th><th className="px-4 py-2.5">Frequency</th><th className="px-4 py-2.5">Next</th>
              <th className="px-4 py-2.5 text-right">Amount</th><th className="px-4 py-2.5 text-right">Raised</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5" />
            </tr></thead>
            <tbody>
              {list.data.map((t) => {
                const amount = (t.lines ?? []).reduce((s: number, l: R) => s + (l.unitPriceMinor ?? 0), 0);
                return (
                  <tr key={t.id} className="cursor-pointer border-b border-ink-50 last:border-0 hover:bg-ink-50/50" onClick={() => setOpen(t)}>
                    <td className="px-4 py-2.5"><div className="text-ink-800">{t.name}</div><div className="text-[11px] text-ink-400">{t.tenancyReference ?? t.propertyName ?? '—'} · {String(t.invoiceType).toLowerCase()}{t.autoIssue ? ' · issues automatically' : ' · raises a draft'}</div></td>
                    <td className="px-4 py-2.5 text-[12px] text-ink-600">{t.intervalCount > 1 ? `Every ${t.intervalCount} ` : ''}{String(t.frequency).toLowerCase()}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap"><span className={t.isDue ? 'font-medium text-amber-700' : ''}><DateText value={t.nextRunOn} /></span></td>
                    <td className="px-4 py-2.5 text-right"><Money minor={amount} /></td>
                    <td className="px-4 py-2.5 text-right text-ink-500">{t.generatedCount}</td>
                    <td className="px-4 py-2.5"><Badge value={t.status} /></td>
                    <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        <Can do="recurringInvoices.update">
                          {t.status === 'ACTIVE'
                            ? <Button size="sm" variant="ghost" onClick={() => setStatusMutation.mutate({ id: t.id, next: 'PAUSED' })}>Pause</Button>
                            : t.status === 'PAUSED' ? <Button size="sm" variant="ghost" onClick={() => setStatusMutation.mutate({ id: t.id, next: 'ACTIVE' })}>Resume</Button> : <span />}
                        </Can>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create */}
      <Modal open={creating} onClose={() => setCreating(false)} size="lg" title="New recurring charge" footer={<>
        <Button variant="secondary" onClick={() => setCreating(false)}>Cancel</Button>
        <Button onClick={() => create.mutate()} disabled={create.isPending || !form.name || !lines.some((l) => l.description && l.unitPrice)}>Create</Button>
      </>}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Company"><Select value={entityId} onChange={(e) => setEntityId(e.target.value)} className="w-full">{entities.data?.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}</Select></Field>
            <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Parking bay B-14" className="w-full" /></Field>
          </div>
          <Field label="Tenancy" hint="Fills in the customer, property and unit"><Select value={form.tenancyId} onChange={(e) => setForm({ ...form, tenancyId: e.target.value })} className="w-full"><option value="">— none —</option>{tenancies.data?.map((t) => <option key={t.id} value={t.id}>{t.reference} · {t.tenants?.[0]?.name} · {t.property?.name} {t.unit?.reference}</option>)}</Select></Field>
          <div className="grid grid-cols-4 gap-3">
            <Field label="Type"><Select value={form.invoiceType} onChange={(e) => setForm({ ...form, invoiceType: e.target.value })} className="w-full">{['RECHARGE', 'SERVICE_CHARGE', 'RENT', 'SALE'].map((t) => <option key={t}>{t}</option>)}</Select></Field>
            <Field label="Every"><Input type="number" min="1" value={form.intervalCount} onChange={(e) => setForm({ ...form, intervalCount: e.target.value })} className="w-full" /></Field>
            <Field label="Frequency"><Select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} className="w-full">{['WEEKLY', 'MONTHLY', 'QUARTERLY', 'ANNUAL'].map((f) => <option key={f}>{f}</option>)}</Select></Field>
            <Field label="Payment terms (days)"><Input type="number" value={form.dueDays} onChange={(e) => setForm({ ...form, dueDays: e.target.value })} className="w-full" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First charge"><Input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} className="w-full" /></Field>
            <Field label="Ends" hint="Blank runs indefinitely"><Input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} className="w-full" /></Field>
          </div>
          <label className="flex items-start gap-2 rounded-xl border border-ink-200 bg-ink-50/60 px-3.5 py-3 text-[12.5px] text-ink-700">
            <input type="checkbox" className="mt-0.5" checked={form.autoIssue} onChange={(e) => setForm({ ...form, autoIssue: e.target.checked })} />
            <span><strong className="font-medium text-ink-900">Issue automatically</strong><span className="block text-ink-500">Posts to the ledger without review. Right for a fee nobody needs to check; leave off and each cycle raises a draft instead.</span></span>
          </label>
          <div>
            <div className="mb-1 flex items-center justify-between"><span className="text-xs font-medium text-ink-600">Lines</span><button onClick={() => setLines([...lines, blankLine()])} className="inline-flex items-center gap-1 text-xs text-bronze-600 hover:underline"><Plus className="h-3.5 w-3.5" /> Add line</button></div>
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={i} className="grid grid-cols-[1fr_130px_110px_28px] items-center gap-2">
                  <Input value={l.description} onChange={(e) => setLines(lines.map((x, k) => (k === i ? { ...x, description: e.target.value } : x)))} placeholder="Description" />
                  <Select value={l.taxCode} onChange={(e) => setLines(lines.map((x, k) => (k === i ? { ...x, taxCode: e.target.value } : x)))}><option value="">Default VAT</option>{codes.map((c) => <option key={c.code} value={c.code}>{c.code} {c.ratePercent}%</option>)}</Select>
                  <Input type="number" step="0.01" value={l.unitPrice} onChange={(e) => setLines(lines.map((x, k) => (k === i ? { ...x, unitPrice: e.target.value } : x)))} placeholder="Amount" />
                  <button onClick={() => setLines(lines.length > 1 ? lines.filter((_, k) => k !== i) : [blankLine()])} className="rounded p-1 text-ink-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
          <Field label="Notes on each invoice"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full" /></Field>
          {create.isError && <p className="text-xs text-red-600">{(create.error as Error).message}</p>}
        </div>
      </Modal>

      {/* Run */}
      <Modal open={!!runResult} onClose={() => setRunResult(null)} size="lg" title={runResult?.dryRun ? 'What would be raised' : 'Invoices raised'} footer={
        runResult?.dryRun && runResult.results.length
          ? <><Button variant="secondary" onClick={() => setRunResult(null)}>Close</Button><Can do="recurringInvoices.run"><Button onClick={() => run.mutate(false)} disabled={run.isPending}>{run.isPending ? 'Raising…' : 'Raise them'}</Button></Can></>
          : <Button variant="secondary" onClick={() => setRunResult(null)}>Close</Button>
      }>
        {runResult && (!runResult.results.length ? (
          <p className="rounded-xl bg-ink-50 px-3.5 py-3 text-[13px] text-ink-600">Nothing is due up to {runResult.upTo}.</p>
        ) : (
          <div className="space-y-2">
            <p className="text-[13px] text-ink-600">{runResult.dryRun ? `${runResult.results.length} invoice(s) would be raised up to ${runResult.upTo}, each dated when it was due.` : `Raised ${runResult.raised}${runResult.failed ? `, ${runResult.failed} failed` : ''}.`}</p>
            <table className="w-full text-[13px]">
              <tbody>
                {runResult.results.map((r: R, i: number) => (
                  <tr key={i} className="border-b border-ink-50 last:border-0">
                    <td className="py-1.5 text-ink-700">{r.name}</td>
                    <td className="py-1.5 text-ink-500">{r.dueOn}</td>
                    <td className="py-1.5 text-right">{r.number ?? (r.error ? <span className="text-red-600">{r.error}</span> : 'draft')}</td>
                    <td className="py-1.5 text-right">{r.totalMinor !== null && <Money minor={r.totalMinor} />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </Modal>

      {/* Detail */}
      <Modal open={!!open} onClose={() => setOpen(null)} size="lg" title={detail.data?.name ?? open?.name ?? 'Recurring charge'} footer={detail.data && (
        <Can do="recurringInvoices.delete"><Button variant="ghost" onClick={() => remove.mutate(detail.data!.id)}>End this template</Button></Can>
      )}>
        {detail.data && (
          <div className="space-y-4">
            <div className="flex items-center justify-between"><Badge value={detail.data.status} /><span className="text-xs text-ink-400">{detail.data.intervalCount > 1 ? `every ${detail.data.intervalCount} ` : ''}{String(detail.data.frequency).toLowerCase()} · next <DateText value={detail.data.nextRunOn} /></span></div>
            <Card title="Lines">
              <ul className="space-y-1 text-[13px]">{(detail.data.lines ?? []).map((l: R, i: number) => (
                <li key={i} className="flex items-center justify-between"><span className="text-ink-700">{l.description}<span className="ml-1.5 text-[11px] text-ink-400">{l.taxCode ?? 'default VAT'}</span></span><Money minor={l.unitPriceMinor} /></li>
              ))}</ul>
            </Card>
            {!!detail.data.generated?.length && (
              <Card title={`Raised so far (${detail.data.generatedCount})`}>
                <table className="w-full text-[12.5px]">
                  <tbody>{detail.data.generated.map((g: R) => (
                    <tr key={g.id} className="border-b border-ink-50 last:border-0"><td className="py-1.5 font-mono text-[12px] text-ink-600">{g.number}</td><td className="py-1.5 text-ink-500"><DateText value={g.issueDate} /></td><td className="py-1.5 text-right"><Money minor={g.totalMinor} currency={g.currency} /></td><td className="py-1.5 pl-3 text-right"><Badge value={g.status} /></td></tr>
                  ))}</tbody>
                </table>
              </Card>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
